import React, { useState, useCallback, useRef, useEffect } from "react";
import moment from "moment";
import pdfMake from "pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import standardPageSizes from "pdfmake/src/standardPageSizes";
import { createStaticMapStyle } from "components/google/maps";
import {
  formatToCurrency,
  formatToNumber,
  formatToPercent,
  getDisclosureValue,
  renderInnerHtml,
  toDataURL,
} from "utils/commonUtils";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const docSize = {
  width: standardPageSizes.LETTER[0],
  height: standardPageSizes.LETTER[1],
};

pdfMake.pageLayout = {
  width: docSize.width,
  height: docSize.height,
  margins: [30, 30],
};

const logoUrl = "/images/logo.png";

function DealPdf({ deal, download }) {
  const [ready, setReady] = useState(false);
  const neighborhoodMapRef = useRef();
  const cityMapRef = useRef();
  const primaryPhotoRef = useRef();
  const logoRef = useRef();
  const logoWhiteBlueRef = useRef();
  const borrowerLogoRef = useRef();
  const secondaryPhotosRef = useRef([]);

  const getImages = useCallback(async () => {
    const { propertyLat: lat, propertyLong: lng } = deal;
    const styles = createStaticMapStyle();

    neighborhoodMapRef.current =
      (await toDataURL(
        `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=16&size=600x300&maptype=roadmap&markers=color:red%7C${lat},${lng}&${styles}&key=AIzaSyBsg9pK5IHbi_DRvleXB84VT1tSSbHMAWs`
      )) || "";
    cityMapRef.current =
      (await toDataURL(
        `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=12&size=600x300&maptype=roadmap&markers=color:red%7C${lat},${lng}&${styles}&key=AIzaSyBsg9pK5IHbi_DRvleXB84VT1tSSbHMAWs`
      )) || "";
    logoRef.current = await toDataURL(logoUrl);
    logoWhiteBlueRef.current = await toDataURL("/images/logo-white-blue.jpg");
    primaryPhotoRef.current = logoRef.current;
    borrowerLogoRef.current = deal.teamId?.companyId?.companyLogoXS
      ? await toDataURL(deal.teamId?.companyId?.companyLogoXS)
      : "";

    if (deal.photos?.length) {
      const primaryPhoto = deal.photos.find((p) => p.isPrimaryPicture);
      const locationCdn = primaryPhoto?.locationCdn;
      primaryPhotoRef.current = locationCdn
        ? await toDataURL(locationCdn)
        : logoRef.current;
      secondaryPhotosRef.current = await Promise.all(
        deal.photos
          .filter((p) => !p.isPrimaryPicture)
          .map((p) => toDataURL(p.locationCdn))
      );
    }
    setReady(true);
  }, [deal, setReady]);

  const minimumLoanAmountRequested = formatToCurrency(
    deal.minimumLoanAmountRequested
  );

  const maximumLoanAmountRequested = formatToCurrency(
    deal.maximumLoanAmountRequested
  );

  const date = moment.utc(deal.createdAt).format("MM/DD/YY");

  const propertyYearBuilt = formatToNumber(deal.propertyYearBuilt);

  const propertySize = formatToNumber(deal.propertySize);

  const propertyNumberOfBuildings = formatToNumber(
    deal.propertyNumberOfBuildings
  );

  const propertyNumberOfFloors = formatToNumber(deal.propertyNumberOfFloors);

  const officeSpacePercentage = formatToPercent(deal.officeSpacePercentage);

  const propertyDescription = renderInnerHtml(deal.propertyDescription);

  const currentBasisOrUses =
    deal.purpose === "Refinance" ? "Current Basis" : "Uses";

  const existingCapitalizationOrSources =
    deal.purpose === "Refinance" ? "Existing Capitalization" : "Sources";

  const sourcePurchasePrice = formatToCurrency(deal.sourcePurchasePrice);

  const sourceLoan = formatToCurrency(deal.sourceLoan);

  const sourceReservesForCapex = formatToCurrency(deal.sourceReservesForCapex);

  const sourceEquity = formatToCurrency(deal.sourceEquity);

  const closingCosts = formatToCurrency(deal.closingCosts);

  const sourceLength =
    deal.sourceUses.length > deal.sourceSources.length
      ? deal.sourceUses.length
      : deal.sourceSources.length;
  const usesAndSources = new Array(sourceLength).fill(null).map((e, i) => {
    const item = [{}, {}];
    if (deal.sourceUses[i]) {
      item[0].label = deal.sourceUses[i].text;
      item[0].value = formatToCurrency(deal.sourceUses[i].amount);
    }
    if (deal.sourceSources[i]) {
      item[1].label = deal.sourceSources[i].text;
      item[1].value = formatToCurrency(deal.sourceSources[i].amount);
    }
    return item;
  });

  const sourceUsesTotal = formatToCurrency(deal.sourceUsesTotal);

  const sourcesTotal = formatToCurrency(deal.sourcesTotal);

  const refinanceExistingLoanPayOff = formatToCurrency(
    deal.refinanceExistingLoanPayOff
  );

  const refinanceNewLoan = formatToCurrency(deal.refinanceNewLoan);

  const refinanceCurrentBasisCapex = formatToCurrency(
    deal.refinanceCurrentBasisCapex
  );

  const refinanceExistingEquity = formatToCurrency(
    deal.refinanceExistingEquity
  );

  const refinanceCurrentBasisClosingCosts = formatToCurrency(
    deal.refinanceCurrentBasisClosingCosts
  );

  const refinanceReturnOfEquity = formatToCurrency(
    deal.refinanceReturnOfEquity || 0
  );

  const refinanceSourceLength =
    deal.refinanceAdditionalCurrentBasisUses.length >
    deal.refinanceAdditionalCurrentBasisSources.length
      ? deal.refinanceAdditionalCurrentBasisUses.length
      : deal.refinanceAdditionalCurrentBasisSources.length;
  const refinanceUsesAndSources = new Array(refinanceSourceLength)
    .fill(null)
    .map((e, i) => {
      const item = [{}, {}];
      if (deal.refinanceAdditionalCurrentBasisUses[i]) {
        item[0].label = deal.refinanceAdditionalCurrentBasisUses[i].text;
        item[0].value = formatToCurrency(
          deal.refinanceAdditionalCurrentBasisUses[i].amount
        );
      }
      if (deal.refinanceAdditionalCurrentBasisSources[i]) {
        item[1].label = deal.refinanceAdditionalCurrentBasisSources[i].text;
        item[1].value = formatToCurrency(
          deal.refinanceAdditionalCurrentBasisSources[i].amount
        );
      }
      return item;
    });

  const refinanceCurrentBasisUsesTotal = formatToCurrency(
    deal.refinanceCurrentBasisUsesTotal || 0
  );

  const refinanceExistingSourcesTotal = formatToCurrency(
    deal.refinanceExistingSourcesTotal || 0
  );

  const approxNetWorthKeyPrincipals = formatToCurrency(
    deal.approxNetWorthKeyPrincipals || 0
  );

  const bankruptcy = getDisclosureValue(deal.bankruptcy);
  const foreclosure = getDisclosureValue(deal.foreclosure);
  const materialLitigation = getDisclosureValue(deal.materialLitigation);
  const condominium = getDisclosureValue(deal.condominium);
  const environmentalConditions = getDisclosureValue(
    deal.environmentalConditions
  );
  const phaseIIEnvironmentalInvestigation = getDisclosureValue(
    deal.phaseIIEnvironmentalInvestigation
  );
  const materialZoningIssues = getDisclosureValue(deal.materialZoningIssues);
  const groundLease = getDisclosureValue(deal.groundLease);

  const docDefinition = {
    pageSize: "LETTER",
    pageMargins: pdfMake.pageLayout.margins,
    info: {
      title: deal.propertyName,
      author: "TermSt.",
    },
    content: [
      // Page 1
      [
        {
          canvas: [
            {
              x: 0,
              y: 0,
              w: docSize.width,
              h: docSize.height,
              type: "rect",
              color: "#0a467f",
            },
          ],
          absolutePosition: { x: 0, y: 0 },
        },
        {
          text: deal.propertyName,
          style: ["header", "center", "white"],
        },
        {
          text: `${maximumLoanAmountRequested} ${deal.loanType || ""}`,
          style: ["subheader", "center", "white"],
        },
        {
          image: "primaryPhoto",
          width: docSize.width - 150,
          style: ["center"],
          margin: [0, 10],
        },
        {
          text: "Presented by:",
          style: ["subheader", "center", "white"],
          margin: [0, 10],
        },
        {
          image: "logoWhiteBlue",
          width: 150,
          absolutePosition: {
            x: docSize.width / 2 - (150 + 150 / 2),
            y: docSize.height - 120,
          },
        },
        borrowerLogoRef.current
          ? {
              image: borrowerLogoRef.current,
              width: 100,
              height: 100,
              absolutePosition: {
                x: docSize.width / 2 + 100 / 2,
                y: docSize.height - 150,
              },
            }
          : {
              text: deal.teamId?.companyId?.companyName || "",
              style: ["header", "white"],
              fontSize: 24,
              absolutePosition: {
                x: docSize.width / 2 + 150 / 2,
                y: docSize.height - 120,
              },
            },
      ],

      // Page 2
      // Next Page
      [
        {
          pageBreak: "before",
          text: "Confidentiality:",
          style: ["normal", "blue"],
          margin: [0, 0, 0, 5],
        },
        {
          text: `This is a confidential Memorandum intended solely for your limited use and benefit in determining whether you desire to express further interest in providing financing for the Property described in this memorandum(the “Property”). This Memorandum contains selected information pertaining to the Property and does not purport to be a representation of the state of affairs of the Property or the owner of the Property (the“Owner”), to be all-inclusive or to contain all or part of the information which prospective investors may require to evaluate financing of real property. All financial projections and information are provided for general reference purposes only and are based on assumptions relating to the general economy, market conditions, competition and other factors beyond the control of the Owner and Term Street, LLC. Therefore,all projections, assumptions and other information provided and made herein are subject to material variation. All references to acreages, square footages, and other measurements are approximations. Additional information and an opportunity to inspect the Property will be made available to interested and qualified prospective parties. In this Memorandum, certain documents, including leases and other materials, are described in summary form. These summaries do not purport to be complete nor necessarily accurate descriptions of the full agreements referenced. Interested parties are expected to review all such summaries and other documents of whatever nature independently and not rely on the contents of this Memorandum in any manner. Neither the Owner or Term Street, LLC, nor any of their respective directors, officers, Affiliates or representatives make any representation or warranty, expressed or implied, as to the accuracy or completeness of this Memorandum or any of its contents, and no legal commitment or obligation shall arise by reason of your receipt of this Memorandum or use of its contents; and you are to rely solely on your investigations and inspections of the Property in evaluating a possible purchase of the real property. The Owner expressly reserved the right, at its sole discretion, to reject any or all expressions of interest or offers to finance the Property, and/or to terminate discussions with any entity at any time with or without notice which may arise as a result of review of this Memorandum. The Owner shall have no legal commitment or obligation to any entity reviewing this Memorandum or making an offer to provide financing for the Property unless and until written agreement(s) for such financing of the Property have been fully executed, delivered and approved by the Owner and any conditions to the Owner’s obligations therein have been satisfied or waived. By receipt of this Memorandum, you agree that this Memorandum and its contents are of a confidential nature, that you will hold and treat it in the strictest confidence and that you will not disclose this Memorandum or any of its contents to any other entity without the prior written authorization of the Owner or Term Street, LLC. You also agree that you will not use this Memorandum or any of its contents in any manner detrimental to the interest of the Owner or Term Street, LLC If after reviewing this Memorandum, you have no further interest in financing the Property, kindly return this Memorandum to Term Street, LLC. This document has been obtained by a user of Term Street, LLC and as such, it is also subject to the Terms of Use and Privacy Policy of the web application at app.termst.com.`,
          style: ["small"],
        },
        {
          text: "Disclaimer:",
          style: ["normal", "blue"],
          margin: [0, 10, 0, 5],
        },
        {
          text: `© 2022 Term Street, LLC. The information contained in this document has been obtained from sources
          believed reliable. While Term Street, LLC does not doubt its accuracy, Term Street, LLC has not verified it and
          makes no guarantee, warranty or representation about it. It is your responsibility to independently confirm its
          accuracy and completeness. Any projections, opinions, assumptions or estimates used are for example only
          and do not represent the current or future performance of the property. The value of this transaction to you
          depends on tax and other factors which should be evaluated by your tax, financial and legal advisors. You
          and your advisors should conduct a careful, independent investigation of the property to determine to your
          satisfaction the suitability of the financing for your needs.`,
          style: ["small"],
        },
        {
          canvas: [
            {
              x: 0,
              y: 0,
              w: docSize.width,
              h: 100,
              type: "rect",
              color: "#0a467f",
            },
          ],
          absolutePosition: { x: 0, y: docSize.height - 100 },
        },
        {
          image: "logoWhiteBlue",
          width: 250,
          absolutePosition: {
            x: docSize.width / 2 - 250 / 2,
            y: docSize.height - 75,
          },
        },
      ],

      // Page 3
      // Next Page
      [
        {
          pageBreak: "before",
          text: "Loan Request:",
          style: ["normal", "blue"],
          margin: [0, 0, 0, 5],
        },
        {
          alignment: "justify",
          columns: [
            [
              {
                text: minimumLoanAmountRequested,
                fontSize: 18,
                bold: true,
              },
              {
                text: "Minimum Requested",
                style: ["normal", "gray"],
              },
            ],
            [
              {
                text: maximumLoanAmountRequested,
                fontSize: 18,
                bold: true,
              },
              {
                text: "Maximum Requested",
                style: ["normal", "gray"],
              },
            ],
          ],
          margin: [0, 0, 0, 10],
        },
        {
          alignment: "justify",
          columns: [
            [
              {
                text: deal.loanType || "-",
                bold: true,
              },
              {
                text: "Loan Type",
                style: ["normal", "gray"],
              },
            ],
            [
              {
                text: deal.purpose || "-",
                bold: true,
              },
              {
                text: "Purpose of Financing",
                style: ["normal", "gray"],
              },
            ],
          ],
          margin: [0, 0, 0, 10],
        },
        {
          alignment: "justify",
          columns: [
            [
              {
                text: date,
                bold: true,
              },
              {
                text: "Date",
                style: ["normal", "gray"],
              },
            ],
          ],
        },
        {
          text: "Additional Information:",
          style: ["normal", "blue"],
          margin: [0, 20, 0, 5],
        },
        {
          text: deal.purposeDescription || "-",
          style: ["small"],
        },
      ],

      // Page 4
      // Next Page
      [
        {
          pageBreak: "before",
          text: "Deal Information",
          style: ["normal"],
        },
        {
          canvas: [
            {
              x: 0,
              y: 0,
              w: docSize.width - 60,
              h: 0.5,
              type: "rect",
              color: "#0a467f",
            },
          ],
          margin: [0, 10],
        },
        {
          text: deal.propertyName,
          fontSize: 18,
          bold: true,
        },
        {
          text: "Property Name",
          style: ["normal", "gray"],
          margin: [0, 0, 0, 10],
        },
        {
          text: deal.propertyAddress,
          bold: true,
        },
        {
          text: "Address",
          style: ["normal", "gray"],
          margin: [0, 0, 0, 10],
        },
        {
          alignment: "justify",
          columns: [
            [
              {
                text: deal.propertyCity,
                bold: true,
              },
              {
                text: "City",
                style: ["normal", "gray"],
              },
            ],
            [
              {
                text: deal.propertyState,
                bold: true,
              },
              {
                text: "State",
                style: ["normal", "gray"],
              },
            ],
            [
              {
                text: deal.propertyZip,
                bold: true,
              },
              {
                text: "Zip Code",
                style: ["normal", "gray"],
              },
            ],
          ],
          margin: [0, 0, 0, 10],
        },
        {
          // line lightgray
          canvas: [
            {
              x: 0,
              y: 0,
              w: docSize.width - 60,
              h: 0.5,
              type: "rect",
              color: "lightgray",
            },
          ],
          margin: [0, 10],
        },
        {
          alignment: "justify",
          columns: [
            [
              {
                text: propertyYearBuilt,
                bold: true,
              },
              {
                text: "Property Built Year",
                style: ["normal", "gray"],
              },
            ],
            [
              {
                text: deal.propertyClass,
                bold: true,
              },
              {
                text: "Property Class",
                style: ["normal", "gray"],
              },
            ],
            { text: "" },
          ],
          margin: [0, 10],
        },
        {
          alignment: "justify",
          columns: [
            [
              {
                text: deal.propertyType,
                bold: true,
              },
              {
                text: "Property Type",
                style: ["normal", "gray"],
              },
            ],
            [
              {
                text: deal.propertySizeType,
                bold: true,
              },
              {
                text: "Size Type",
                style: ["normal", "gray"],
              },
            ],
            [
              {
                text: propertySize,
                bold: true,
              },
              {
                text: "Amount",
                style: ["normal", "gray"],
              },
            ],
          ],
          margin: [0, 10],
        },
        {
          alignment: "justify",
          columns: [
            [
              {
                text: propertyNumberOfBuildings,
                bold: true,
              },
              {
                text: "Number of Buildings",
                style: ["normal", "gray"],
              },
            ],
            [
              {
                text: propertyNumberOfFloors,
                bold: true,
              },
              {
                text: "Number of Floors",
                style: ["normal", "gray"],
              },
            ],
            { text: "" },
          ],
          margin: [0, 0, 0, 10],
        },
        {
          alignment: "justify",
          columns: [
            [
              {
                text: officeSpacePercentage,
                bold: true,
              },
              {
                text: "Office Space Percentage",
                style: ["normal", "gray"],
              },
            ],
            { text: "" },
            { text: "" },
          ],
          margin: [0, 0, 0, 10],
        },
        {
          text: "Property Description:",
          style: ["normal", "blue"],
          margin: [0, 10, 0, 5],
        },
        {
          text: propertyDescription || "-",
          style: ["small"],
        },
      ],

      // Page 5
      // Next Page
      [
        {
          pageBreak: "before",
          text: "Sources & Uses:",
          style: ["normal", "blue"],
          margin: [0, 0, 0, 5],
        },
        {
          text: "Existing Capitalization",
          fontSize: 18,
          bold: true,
          margin: [0, 5, 0, 10],
        },
        {
          alignment: "justify",
          columns: [
            {
              text: currentBasisOrUses,
              bold: true,
            },
            {
              text: existingCapitalizationOrSources,
              bold: true,
            },
            { text: "" },
          ],
          margin: [0, 0, 0, 10],
        },
        {
          alignment: "justify",
          columns: [
            [
              {
                text: sourcePurchasePrice,
                bold: true,
              },
              {
                text: "Purchase Price",
                style: ["normal", "gray"],
              },
            ],
            [
              {
                text: sourceLoan,
                bold: true,
              },
              {
                text: "Loan",
                style: ["normal", "gray"],
              },
            ],
            { text: "" },
          ],
          margin: [0, 0, 0, 10],
        },
        {
          alignment: "justify",
          columns: [
            [
              {
                text: sourceReservesForCapex,
                bold: true,
              },
              {
                text: "Previous Capital Expenditures",
                style: ["normal", "gray"],
              },
            ],
            [
              {
                text: sourceEquity,
                bold: true,
              },
              {
                text: "Equity",
                style: ["normal", "gray"],
              },
            ],
            { text: "" },
          ],
          margin: [0, 0, 0, 10],
        },
        {
          alignment: "justify",
          columns: [
            [
              {
                text: closingCosts,
                bold: true,
              },
              {
                text: "Closing Costs",
                style: ["normal", "gray"],
              },
            ],
            { text: "" },
            { text: "" },
          ],
          margin: [0, 0, 0, 10],
        },
        ...usesAndSources.map((item) => ({
          alignment: "justify",
          columns: [
            [
              {
                text: item[0].value,
                bold: true,
              },
              {
                text: item[0].label,
                style: ["normal", "gray"],
              },
            ],
            [
              {
                text: item[1].value || "",
                bold: true,
              },
              {
                text: item[1].label || "",
                style: ["normal", "gray"],
              },
            ],
            { text: "" },
          ],
          margin: [0, 0, 0, 10],
        })),
        {
          // line lightgray
          canvas: [
            {
              x: 0,
              y: 0,
              w: docSize.width - 60,
              h: 0.5,
              type: "rect",
              color: "lightgray",
            },
          ],
          margin: [0, 0, 0, 10],
        },

        {
          alignment: "justify",
          columns: [
            [
              {
                text: sourceUsesTotal,
                bold: true,
              },
              {
                text: "Total",
                style: ["normal", "gray"],
              },
            ],
            [
              {
                text: sourcesTotal,
                bold: true,
              },
              {
                text: "Total",
                style: ["normal", "gray"],
              },
            ],
            { text: "" },
          ],
          margin: [0, 0],
        },
        {
          // line lightgray
          canvas: [
            {
              x: 0,
              y: 0,
              w: docSize.width - 60,
              h: 0.5,
              type: "rect",
              color: "lightgray",
            },
          ],
          margin: [0, 10],
        },
        deal.purpose === "Refinance"
          ? [
              {
                text: "Post-Closing Capitalization",
                fontSize: 18,
                bold: true,
                margin: [0, 0, 0, 10],
              },
              {
                alignment: "justify",
                columns: [
                  {
                    text: currentBasisOrUses,
                    bold: true,
                  },
                  {
                    text: existingCapitalizationOrSources,
                    bold: true,
                  },
                  { text: "" },
                ],
                margin: [0, 0, 0, 10],
              },
              {
                alignment: "justify",
                columns: [
                  [
                    {
                      text: refinanceExistingLoanPayOff,
                      bold: true,
                    },
                    {
                      text: "Payoff Existing Loan",
                      style: ["normal", "gray"],
                    },
                  ],
                  [
                    {
                      text: refinanceNewLoan,
                      bold: true,
                    },
                    {
                      text: "New Loan",
                      style: ["normal", "gray"],
                    },
                  ],
                  { text: "" },
                ],
                margin: [0, 0, 0, 10],
              },
              {
                alignment: "justify",
                columns: [
                  [
                    {
                      text: refinanceCurrentBasisCapex,
                      bold: true,
                    },
                    {
                      text: "Planned Capital Expenditures",
                      style: ["normal", "gray"],
                    },
                  ],
                  [
                    {
                      text: refinanceExistingEquity,
                      bold: true,
                    },
                    {
                      text: "Equity",
                      style: ["normal", "gray"],
                    },
                  ],
                  { text: "" },
                ],
                margin: [0, 0, 0, 10],
              },
              {
                alignment: "justify",
                columns: [
                  [
                    {
                      text: refinanceCurrentBasisClosingCosts,
                      bold: true,
                    },
                    {
                      text: "Closing Costs",
                      style: ["normal", "gray"],
                    },
                  ],
                  [
                    {
                      text: refinanceReturnOfEquity,
                      bold: true,
                    },
                    {
                      text: "Return of Equity",
                      style: ["normal", "gray"],
                    },
                  ],
                  { text: "" },
                ],
                margin: [0, 0, 0, 10],
              },
              ...refinanceUsesAndSources.map((item) => ({
                alignment: "justify",
                columns: [
                  [
                    {
                      text: item[0].value,
                      bold: true,
                    },
                    {
                      text: item[0].label,
                      style: ["normal", "gray"],
                    },
                  ],
                  [
                    {
                      text: item[1].value || "",
                      bold: true,
                    },
                    {
                      text: item[1].label || "",
                      style: ["normal", "gray"],
                    },
                  ],
                  { text: "" },
                ],
                margin: [0, 0, 0, 10],
              })),
              {
                // line lightgray
                canvas: [
                  {
                    x: 0,
                    y: 0,
                    w: docSize.width - 60,
                    h: 0.5,
                    type: "rect",
                    color: "lightgray",
                  },
                ],
                margin: [0, 0, 0, 10],
              },
              {
                alignment: "justify",
                columns: [
                  [
                    {
                      text: refinanceCurrentBasisUsesTotal,
                      bold: true,
                    },
                    {
                      text: "Total",
                      style: ["normal", "gray"],
                    },
                  ],
                  [
                    {
                      text: refinanceExistingSourcesTotal,
                      bold: true,
                    },
                    {
                      text: "Total",
                      style: ["normal", "gray"],
                    },
                  ],
                  { text: "" },
                ],
                margin: [0, 0],
              },
            ]
          : [],
      ],

      // Page 6
      // Next Page
      [
        {
          pageBreak: "before",
          text: "Neighborhood Map:",
          style: ["normal", "blue"],
          margin: [0, 0, 0, 5],
        },
        {
          image: "neighborhoodMap",
          width: docSize.width,
          style: ["center"],
          margin: [0, 10],
        },
        {
          text: "City Map:",
          style: ["normal", "blue"],
          margin: [0, 10, 0, 5],
        },
        {
          image: "cityMap",
          width: docSize.width,
          style: ["center"],
          margin: [0, 10],
        },
      ],

      // Page 7
      // Next Page
      [
        {
          pageBreak: "before",
          text: "About the Sponsor:",
          style: ["normal", "blue"],
          margin: [0, 0, 0, 5],
        },
        {
          alignment: "justify",
          columns: [
            [
              {
                text: deal.sponsorName || "-",
                fontSize: 18,
                bold: true,
              },
              {
                text: "Sponsor Name",
                style: ["normal", "gray"],
              },
            ],
          ],
          margin: [0, 5, 0, 10],
        },
        // line lightgray
        {
          canvas: [
            {
              x: 0,
              y: 0,
              w: docSize.width - 60,
              h: 0.5,
              type: "rect",
              color: "lightgray",
            },
          ],
          margin: [0, 30, 0, 10],
        },
        {
          alignment: "justify",
          columns: [
            [
              {
                text: deal.netWorthKeyPrincipals || "-",
                bold: true,
              },
              {
                text: "Net Worth Key Principals",
                style: ["normal", "gray"],
              },
            ],
            [
              {
                text: deal.liquidityOfKeyPrincipals || "-",
                bold: true,
              },
              {
                text: "Liquidity Key Principals",
                style: ["normal", "gray"],
              },
            ],
          ],
          margin: [0, 0, 0, 10],
        },
        {
          alignment: "justify",
          columns: [
            [
              {
                text: approxNetWorthKeyPrincipals,
                bold: true,
              },
              {
                text: "Approximate Liquidity Key Principals",
                style: ["normal", "gray"],
              },
            ],
            { text: "" },
          ],
          margin: [0, 0, 0, 10],
        },
        {
          text: "Key Principal Bios:",
          style: ["normal", "blue"],
          margin: [0, 10, 0, 5],
        },
        {
          text: deal.keyPrincipleBios || "-",
          style: ["small"],
        },
      ],

      // Page 8
      // Next Page
      [
        {
          pageBreak: "before",
          text: "Management Company:",
          style: ["normal", "blue"],
          margin: [0, 0, 0, 5],
        },
        {
          alignment: "justify",
          columns: [
            [
              {
                text: deal.managementCompanyName || "-",
                fontSize: 18,
                bold: true,
              },
              {
                text: "Manager Name",
                style: ["normal", "gray"],
              },
            ],
          ],
          margin: [0, 5, 0, 10],
        },
        // line lightgray
        {
          canvas: [
            {
              x: 0,
              y: 0,
              w: docSize.width - 60,
              h: 0.5,
              type: "rect",
              color: "lightgray",
            },
          ],
          margin: [0, 30, 0, 10],
        },
        {
          alignment: "justify",
          columns: [
            [
              {
                text: deal.managementCompanyAffiliate || "-",
                bold: true,
              },
              {
                text: "Management Company an Affiliate of Sponsor?",
                style: ["normal", "gray"],
              },
            ],
          ],
          margin: [0, 0, 0, 10],
        },
        {
          text: "Management Company Bio:",
          style: ["normal", "blue"],
          margin: [0, 10, 0, 5],
        },
        {
          text: deal.managementCompanyBio || "-",
          style: ["small"],
        },
      ],

      // Page 9
      // Next Page
      [
        {
          pageBreak: "before",
          text: "Disclosures:",
          style: ["normal", "blue"],
          margin: [0, 0, 0, 5],
        },
        {
          text: "The Sponsor has not disclosed anything else that might impact your underwriting.",
          style: ["normal", "gray"],
          margin: [0, 5, 0, 10],
        },
        {
          alignment: "justify",
          columns: [
            [
              {
                text: bankruptcy,
                bold: true,
              },
              {
                text: "Has anyone in the ownership structure ever filed for bankrupcy either personally or in a business entity?",
                style: ["normal", "gray"],
              },
            ],
          ],
          margin: [0, 0, 0, 10],
        },
        // line lightgray
        {
          canvas: [
            {
              x: 0,
              y: 0,
              w: docSize.width - 60,
              h: 0.5,
              type: "rect",
              color: "lightgray",
            },
          ],
          margin: [0, 10],
        },
        {
          alignment: "justify",
          columns: [
            [
              {
                text: foreclosure,
                bold: true,
              },
              {
                text: "Has anyone in the ownership structure ever lost a property to foreclosure, deed in lieu of foreclosure, or any other property taking action?",
                style: ["normal", "gray"],
              },
            ],
          ],
          margin: [0, 0, 0, 10],
        },
        // line lightgray
        {
          canvas: [
            {
              x: 0,
              y: 0,
              w: docSize.width - 60,
              h: 0.5,
              type: "rect",
              color: "lightgray",
            },
          ],
          margin: [0, 10],
        },
        {
          alignment: "justify",
          columns: [
            [
              {
                text: materialLitigation,
                bold: true,
              },
              {
                text: "Will a search of any of the ownership entities or Individuals turn up an material litigation?",
                style: ["normal", "gray"],
              },
            ],
          ],
          margin: [0, 0, 0, 10],
        },
        // line lightgray
        {
          canvas: [
            {
              x: 0,
              y: 0,
              w: docSize.width - 60,
              h: 0.5,
              type: "rect",
              color: "lightgray",
            },
          ],
          margin: [0, 10],
        },
        {
          alignment: "justify",
          columns: [
            [
              {
                text: condominium,
                bold: true,
              },
              {
                text: "Is any part of the property a condominium?",
                style: ["normal", "gray"],
              },
            ],
          ],
          margin: [0, 0, 0, 10],
        },
        // line lightgray
        {
          canvas: [
            {
              x: 0,
              y: 0,
              w: docSize.width - 60,
              h: 0.5,
              type: "rect",
              color: "lightgray",
            },
          ],
          margin: [0, 10],
        },
        {
          alignment: "justify",
          columns: [
            [
              {
                text: environmentalConditions,
                bold: true,
              },
              {
                text: "Are there any known environmental conditions at the property?",
                style: ["normal", "gray"],
              },
            ],
          ],
          margin: [0, 0, 0, 10],
        },
        // line lightgray
        {
          canvas: [
            {
              x: 0,
              y: 0,
              w: docSize.width - 60,
              h: 0.5,
              type: "rect",
              color: "lightgray",
            },
          ],
          margin: [0, 10],
        },
        {
          alignment: "justify",
          columns: [
            [
              {
                text: phaseIIEnvironmentalInvestigation,
                bold: true,
              },
              {
                text: "Has a phase II environmental investigation ever been recommended or completed?",
                style: ["normal", "gray"],
              },
            ],
          ],
          margin: [0, 0, 0, 10],
        },
        // line lightgray
        {
          canvas: [
            {
              x: 0,
              y: 0,
              w: docSize.width - 60,
              h: 0.5,
              type: "rect",
              color: "lightgray",
            },
          ],
          margin: [0, 10],
        },
        {
          alignment: "justify",
          columns: [
            [
              {
                text: materialZoningIssues,
                bold: true,
              },
              {
                text: "Are there any material zoning issues with the property?",
                style: ["normal", "gray"],
              },
            ],
          ],
          margin: [0, 0, 0, 10],
        },
        // line lightgray
        {
          canvas: [
            {
              x: 0,
              y: 0,
              w: docSize.width - 60,
              h: 0.5,
              type: "rect",
              color: "lightgray",
            },
          ],
          margin: [0, 10],
        },
        {
          alignment: "justify",
          columns: [
            [
              {
                text: groundLease,
                bold: true,
              },
              {
                text: "Is the property on a ground lease?",
                style: ["normal", "gray"],
              },
            ],
          ],
          margin: [0, 0, 0, 10],
        },
      ],

      // Page 10
      // Next Page
      secondaryPhotosRef.current.length
        ? [
            {
              pageBreak: "before",
              text: "Pictures:",
              style: ["normal", "blue"],
              margin: [0, 0, 0, 5],
            },
            ...secondaryPhotosRef.current.map((dataUrl) => ({
              image: dataUrl,
              width: docSize.width - 60,
              style: ["center"],
              margin: [0, 10],
            })),
          ]
        : [],

      // Last Page
      // Next Page
      [
        {
          pageBreak: "before",
          text: "Thank You Your Interest!",
          style: ["header", "center", "blue"],
        },
        {
          image: "logo",
          width: docSize.width - 250,
          style: ["center"],
          margin: [0, 120],
        },
        {
          text: "If you’d like to learn more about how TermStreet can help you close more transactions powered by a more efficient process, please reach out.",
          style: ["normal", "center", "blue"],
          margin: [0, 10, 0, 50],
        },
        {
          text: "Matt Bertram",
          style: ["subheader", "center", "blue"],
          bold: false,
          margin: [0, 0],
        },
        {
          text: "Founder",
          style: ["subheader", "center", "blue"],
          bold: false,
          margin: [0, 0],
        },
        {
          text: "404-797-0459",
          style: ["subheader", "center", "blue"],
          bold: false,
          margin: [0, 0],
        },
        {
          text: "matt@termst.com",
          style: ["subheader", "center", "blue"],
          bold: false,
          margin: [0, 0],
        },
        {
          text: "termst.com",
          style: ["subheader", "center", "blue"],
          bold: false,
          margin: [0, 10],
        },
      ],
    ],
    defaultStyle: {
      columnGap: 20,
    },
    styles: {
      header: {
        margin: [0, 10],
        fontSize: 18,
        bold: true,
      },
      subheader: {
        margin: [0, 5],
        fontSize: 16,
        bold: true,
      },
      quote: {
        italics: true,
      },
      normal: {
        fontSize: 12,
      },
      small: {
        fontSize: 10,
      },
      center: {
        alignment: "center",
      },
      white: {
        color: "white",
      },
      blue: {
        color: "#0a467f",
      },
      gray: {
        color: "gray",
      },
    },
    images: {
      logo: logoRef.current,
      logoWhiteBlue: logoWhiteBlueRef.current,
      neighborhoodMap: neighborhoodMapRef.current,
      cityMap: cityMapRef.current,
      primaryPhoto: primaryPhotoRef.current,
    },
  };

  const previewInIFrame = () => {
    const pdfDocGenerator = pdfMake.createPdf(docDefinition);
    pdfDocGenerator.getDataUrl((dataUrl) => {
      const targetElement = document.querySelector("#dealPdfIFrameContainer");
      const iframe = document.createElement("iframe");
      iframe.src = dataUrl;
      targetElement.appendChild(iframe);
    });
  };

  const downloadPdf = () => {
    pdfMake.createPdf(docDefinition).download(deal.propertyName, window.close);
  };

  useEffect(() => {
    getImages();
  }, [getImages]);

  useEffect(() => {
    if (ready) {
      if (download) {
        downloadPdf();
      } else {
        previewInIFrame();
      }
    }
  }, [ready]);

  return <div id="dealPdfIFrameContainer"></div>;
}

export default DealPdf;
